/* eslint-disable @typescript-eslint/no-explicit-any */
import copyToClipboard from 'copy-to-clipboard';
import { SELECT_COLUMN_KEY } from '../Columns';
import { DataGridRef } from '../types';
import { isRangeSelected } from './selectRange';

const VALID_MIN_COLUMN_INDEX = 1;

function defaultOnCopy<R>(gridRefContext: DataGridRef<R>) {
  const { selectedRange, selectedRows, selectedColumns, rows, columns, rowKeyGetter } =
    gridRefContext;
  const copyArr: (string | number)[] = [];
  const contentStartColIdx = columns.findIndex((col) => col.key !== SELECT_COLUMN_KEY);
  const validColumns = columns.slice(contentStartColIdx);
  if (isRangeSelected(selectedRange)) {
    const { columnIdx, rowIdx, endColumnIdx, endRowIdx } = selectedRange;
    const _beginIdx = Math.min(Math.max(VALID_MIN_COLUMN_INDEX, columnIdx), endColumnIdx);
    const _endIdx = Math.max(Math.max(VALID_MIN_COLUMN_INDEX, columnIdx), endColumnIdx);
    const _beginRowIdx = Math.min(rowIdx, endRowIdx);
    const _endRowIdx = Math.max(rowIdx, endRowIdx);
    /**
     * 拷贝选中的情况
     */
    for (let i = _beginRowIdx; i <= _endRowIdx; i++) {
      const row: R = rows[i];
      const rowValue = [];
      for (let j = _beginIdx; j <= _endIdx; j++) {
        const columnKey = columns[j].key;
        rowValue.push((row as Record<string, any>)[columnKey]?.replace?.(/\x00/g, ''));
      }
      copyArr.push(rowValue.join('\t'));
    }
  } else if (selectedRows?.size) {
    /**
     * 拷贝整行的情况
     */
    for (let i = 0; i < rows.length; i++) {
      /**
       * 比较废性能，需要遍历所有的row
       */
      const row: R = rows[i];
      if (row && rowKeyGetter) {
        const rowKey = rowKeyGetter(row);
        if (selectedRows.has(rowKey)) {
          copyArr.push(
            validColumns
              .map(({ key }) => {
                return (row as Record<string, any>)[key]?.replace?.(/\x00/g, '');
              })
              .join('\t'),
          );
        }
      }
    }
  } else if (selectedColumns?.size) {
    const selectedSortedColumns = [...selectedColumns].sort(
      (leftColumnKey: string, rightColumnKey: string) => {
        const leftIdx = columns.findIndex((c) => c.key === leftColumnKey);
        const rightIdx = columns.findIndex((c) => c.key === rightColumnKey);
        return leftIdx - rightIdx;
      },
    );
    for (let i = 0; i < rows.length; i++) {
      /**
       * 复制整列
       */
      const row: R = rows[i];
      const rowStr = selectedSortedColumns
        .map((columnKey) => {
          return (row as Record<string, any>)[columnKey]?.replace?.(/\x00/g, '');
        })
        .join('\t');
      copyArr.push(rowStr);
    }
  }
  const copyStr = copyArr.join('\n');
  copyToClipboard(copyStr, {
    format: 'text/plain',
  });
}

function isSelectedRows<R>(gridRefContext: DataGridRef<R>) {
  return !!gridRefContext?.selectedRows?.size;
}

function eachRows<R>(
  gridRefContext: DataGridRef<R>,
  rowRange: {
    minIndex: number;
    maxIndex: number;
  },
  callback: (row: R) => void,
) {
  const { rows } = gridRefContext;
  const { minIndex, maxIndex } = rowRange;
  for (let i = minIndex; i <= maxIndex; i++) {
    callback(rows[i]);
  }
}

function getSelectedRowsData<R>(gridRefContext: DataGridRef<R>) {
  const { rows, columns, selectedRows, rowKeyGetter } = gridRefContext;
  const targetColumns = columns?.filter((col) => col.key !== SELECT_COLUMN_KEY);
  const [minRowIdx, maxRowIdx] = [0, rows.length - 1];
  const data = [];
  /**
   * header
   */
  data.push(
    targetColumns.map((column) => {
      return column.name;
    }),
  );

  eachRows(
    gridRefContext,
    {
      minIndex: minRowIdx,
      maxIndex: maxRowIdx,
    },
    (row: R) => {
      const rowKey = rowKeyGetter(row as Record<string, any>);
      if (selectedRows?.has(rowKey)) {
        data.push(
          targetColumns.map((column) => {
            return (row as Record<string, any>)[column.key];
          }),
        );
      }
    },
  );
  return data;
}

function getSelectedRangeData<R>(gridRefContext: DataGridRef<R>) {
  const { selectedRange, columns } = gridRefContext;
  const [minIdx, maxIdx] = [selectedRange.columnIdx, selectedRange.endColumnIdx].sort(
    (a, b) => a - b,
  );
  const [minRowIdx, maxRowIdx] = [selectedRange.rowIdx, selectedRange.endRowIdx].sort(
    (a, b) => a - b,
  );
  if (isSelectedRows(gridRefContext)) {
    return getSelectedRowsData(gridRefContext);
  }

  if (minIdx < 0 || minRowIdx < 0) {
    return null;
  }
  const data = [];
  const targetColumns = columns.slice(minIdx, maxIdx + 1);
  /**
   * header
   */
  data.push(
    targetColumns.map((column) => {
      return column.name;
    }),
  );

  eachRows(
    gridRefContext,
    {
      minIndex: minRowIdx,
      maxIndex: maxRowIdx,
    },
    (row: R) => {
      data.push(
        targetColumns.map((column) => {
          return (row as Record<string, any>)[column.key];
        }),
      );
    },
  );
  return data;
}

export { defaultOnCopy, getSelectedRangeData };
